// Uncertainty for the displayed rain total: "given radar total R here, what
// would a rain gauge at this spot have measured?" Pure ES module (no DOM, no
// deps) so it runs in the rain worker, on the main thread, in Vitest and in the
// validation harness (tools/rain-validation), which calls this exact file to
// score held-out events.
//
// The model (errorModel.json, fitted by tools/rain-validation/py/errormodel.py)
// predicts quantiles of q = ln((G + eps) / (R + eps)), eps = 0.254 mm, where G
// is the gauge total and R the radar total over the same window. Each quantile
// is a linear function of a few named features (FEATURES below), shifted by a
// leave-one-event-out conformal correction so held-out events are covered at
// the nominal rate, then sorted (no crossing) and mapped back to mm:
//   G_p = max(0, (R + eps) * exp(q_p) - eps)

import { cellToLatLon } from "./grid.js";

export const EPS_MM = 0.254;
const DEG = Math.PI / 180;
const EARTH_R_KM = 6371.0088;
const MS_PER_HOUR = 3600 * 1000;

// ---------------------------------------------------------------------------
// Per-run covariates

export const RUN_COV_DEFAULTS = {
  maxFrames: 48, // rate statistics use at most this many frames, evenly spaced
  cellStride: 3, // ...and every 3rd row and column (1/9 of the cells)
  wetRate: 1, // mm/h: a cell "raining" in a frame
  convRate: 25, // mm/h: rain volume at or above this counts as convective
  wetTotal: 1, // mm: a cell "wet" in the total
  maxGapMs: 20 * 60 * 1000, // same rule as accumulate.js MAX_GAP_MS
};

// Log-spaced histogram for rates >= 1 mm/h: 40 bins per decade (~6% wide).
const HIST_PER_DECADE = 40;
const HIST_BINS = 4 * HIST_PER_DECADE; // 1 .. 10^4 mm/h
const histBin = (v) => Math.min(HIST_BINS - 1, Math.max(0, Math.floor(HIST_PER_DECADE * Math.log10(v))));
const binValue = (b) => 10 ** ((b + 0.5) / HIST_PER_DECADE);

function histQuantile(hist, n, p) {
  if (!n) return 0;
  const target = p * n;
  let acc = 0;
  for (let b = 0; b < hist.length; b++) {
    acc += hist[b];
    if (acc >= target) return binValue(b);
  }
  return binValue(hist.length - 1);
}

function quantileSorted(sorted, p) {
  if (!sorted.length) return 0;
  const x = p * (sorted.length - 1);
  const i = Math.floor(x);
  const j = Math.min(sorted.length - 1, i + 1);
  return sorted[i] + (x - i) * (sorted[j] - sorted[i]);
}

function dayOfYear(ms) {
  const d = new Date(ms);
  return Math.floor((ms - Date.UTC(d.getUTCFullYear(), 0, 1)) / 86400000) + 1;
}

/**
 * Scalars describing one rain run (window), computed from what the worker
 * already holds. Nothing here needs a gauge or MRMS.
 *
 * frames: [{ tMs, rate }] sorted by time (rate: Float32Array mm/h, NaN outside
 *   coverage, or a function returning one). Bracketing frames may be included;
 *   only frames inside [startMs, endMs] feed the rate statistics (if fewer than
 *   two are inside, the frames of the pairs overlapping the window are used).
 * total: Float32Array run total in mm (NaN outside coverage).
 * grid: from grid.js createRainGrid. gaps: [{ startMs, endMs }] from
 * accumulate(). station: { id, lat, lon } (the radar; falls back to grid).
 *
 * Cost: at most `maxFrames` frames are read (evenly spaced by index) and only
 * every `cellStride`-th row/column, so 288 frames of 950x950 read 48 frames x
 * ~100k cells; the time is dominated by unpacking those 48 frames.
 */
export function computeRunCovariates({ frames = [], total = null, grid, startMs, endMs, gaps = [], station = null }, opts = {}) {
  const o = { ...RUN_COV_DEFAULTS, ...opts };
  const { nx, ny } = grid;
  const windowMs = Math.max(1, endMs - startMs);

  let gapMs = 0;
  for (const g of gaps || []) gapMs += Math.max(0, Math.min(endMs, g.endMs) - Math.max(startMs, g.startMs));

  // Scans inside the window and the intervals of the pairs that overlap it.
  const inside = [];
  const pairFrames = new Set();
  const intervals = [];
  for (let i = 0; i < frames.length; i++) {
    const t = frames[i].tMs;
    if (t >= startMs && t <= endMs) inside.push(i);
    if (i + 1 < frames.length) {
      const t1 = frames[i + 1].tMs;
      if (t1 > startMs && t < endMs) {
        const dt = t1 - t;
        if (dt > 0 && dt <= o.maxGapMs) {
          intervals.push(dt / 60000);
          pairFrames.add(i).add(i + 1);
        }
      }
    }
  }
  const meanInterval = intervals.length ? intervals.reduce((a, b) => a + b, 0) / intervals.length : NaN;
  const maxInterval = intervals.length ? Math.max(...intervals) : NaN;

  // Rate statistics on a frame/cell subsample.
  let use = inside.length >= 2 ? inside : [...pairFrames].sort((a, b) => a - b);
  if (use.length > o.maxFrames) {
    const step = use.length / o.maxFrames;
    use = Array.from({ length: o.maxFrames }, (_, k) => use[Math.min(use.length - 1, Math.floor((k + 0.5) * step))]);
  }
  const hist = new Float64Array(HIST_BINS);
  let covered = 0;
  let wet = 0;
  let vol = 0;
  let convVol = 0;
  for (const fi of use) {
    const f = frames[fi];
    const rate = typeof f.rate === "function" ? f.rate() : f.rate;
    if (!rate) continue;
    for (let row = (o.cellStride >> 1); row < ny; row += o.cellStride) {
      const base = row * nx;
      for (let col = (o.cellStride >> 1); col < nx; col += o.cellStride) {
        const v = rate[base + col];
        if (!(v >= 0)) continue; // NaN = outside coverage
        covered++;
        if (v >= o.wetRate) {
          wet++;
          vol += v;
          if (v >= o.convRate) convVol += v;
          hist[histBin(v)]++;
        }
      }
    }
  }

  // Total-grid statistics on the same cell subsample.
  let tCovered = 0;
  const wetTotals = [];
  let tMax = 0;
  if (total) {
    for (let row = (o.cellStride >> 1); row < ny; row += o.cellStride) {
      const base = row * nx;
      for (let col = (o.cellStride >> 1); col < nx; col += o.cellStride) {
        const v = total[base + col];
        if (!(v >= 0)) continue;
        tCovered++;
        if (v >= o.wetTotal) wetTotals.push(v);
        if (v > tMax) tMax = v;
      }
    }
  }
  wetTotals.sort((a, b) => a - b);

  const mid = (startMs + endMs) / 2;
  const month = new Date(mid).getUTCMonth() + 1;
  const doy = dayOfYear(mid);
  const lat = station?.lat ?? grid.lat;
  const lon = station?.lon ?? grid.lon;
  return {
    windowHours: windowMs / MS_PER_HOUR,
    gapFraction: Math.min(1, gapMs / windowMs),
    nScans: inside.length,
    meanScanIntervalMin: meanInterval,
    maxScanIntervalMin: maxInterval,
    framesSampled: use.length,
    wetAreaFrac: covered ? wet / covered : 0,
    convFrac: vol > 0 ? convVol / vol : 0,
    rateP50Wet: histQuantile(hist, wet, 0.5),
    rateP90Wet: histQuantile(hist, wet, 0.9),
    rateP95Wet: histQuantile(hist, wet, 0.95),
    rateP99Wet: histQuantile(hist, wet, 0.99),
    totalWetFrac: tCovered ? wetTotals.length / tCovered : 0,
    totalP50Wet: quantileSorted(wetTotals, 0.5),
    totalP95Wet: quantileSorted(wetTotals, 0.95),
    totalMaxMm: tMax,
    month,
    dayOfYear: doy,
    // +1 in mid-January, -1 in mid-July (northern-hemisphere season)
    seasonCos: Math.cos((2 * Math.PI * (doy - 15)) / 365.25),
    coolSeason: month >= 11 || month <= 4 ? 1 : 0,
    stationLat: lat,
    stationLon: lon,
    stationId: station?.id ?? null,
    // Terrain blockage of the radar's low beams (radarTerrain.json), passed in
    // by the caller on `station`; NaN when unknown.
    beamRaiseDeg: typeof station?.beamRaiseDeg === "number" ? station.beamRaiseDeg : NaN,
  };
}

// ---------------------------------------------------------------------------
// Per-cell covariates

function haversineKm(lat1, lon1, lat2, lon2) {
  const p1 = lat1 * DEG;
  const p2 = lat2 * DEG;
  const a = Math.sin((p2 - p1) / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(((lon2 - lon1) * DEG) / 2) ** 2;
  return 2 * EARTH_R_KM * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Inputs known at hover time for one grid cell (col, row are rounded to the
 * containing cell). rangeKm is the great-circle distance from the radar (the
 * grid center) to the cell center. localCv is the coefficient of variation of
 * the total over the (2*radius+1)^2 block (5x5 = 2.5 km by default):
 * sd / (mean + eps).
 */
export function cellCovariates({ total, grid, col, row, radius = 2 }) {
  const c = Math.round(col);
  const r = Math.round(row);
  const { nx, ny } = grid;
  const inGrid = c >= 0 && r >= 0 && c < nx && r < ny;
  const ll = cellToLatLon(grid, c, r);
  const rangeKm = haversineKm(grid.lat, grid.lon, ll.lat, ll.lon);
  let n = 0;
  let s = 0;
  let ss = 0;
  let mx = 0;
  for (let dr = -radius; dr <= radius; dr++) {
    const rr = r + dr;
    if (rr < 0 || rr >= ny) continue;
    for (let dc = -radius; dc <= radius; dc++) {
      const cc = c + dc;
      if (cc < 0 || cc >= nx) continue;
      const v = total[rr * nx + cc];
      if (!(v >= 0)) continue;
      n++;
      s += v;
      ss += v * v;
      if (v > mx) mx = v;
    }
  }
  const mean = n ? s / n : NaN;
  const sd = n > 1 ? Math.sqrt(Math.max(0, (ss - (s * s) / n) / (n - 1))) : 0;
  return {
    totalMm: inGrid ? total[r * nx + c] : NaN,
    rangeKm,
    localMeanMm: mean,
    localCv: n ? sd / (mean + EPS_MM) : NaN,
    localMaxMm: n ? mx : NaN,
  };
}

// ---------------------------------------------------------------------------
// Interval

// Feature catalog shared with tools/rain-validation/py/errormodel.py (keep the
// two in sync; the harness checks that Python and JS predictions agree).
// x holds the model inputs after clamping to the training support.
const LN_1 = Math.log(1 + EPS_MM);
const LN_10 = Math.log(10 + EPS_MM);
// Terrain blockage above ~0.5 deg is seen in a single validation storm
// (KVTX); cap it so one event cannot drive a straight-line extrapolation.
export const TERRAIN_CAP_DEG = 0.5;
export const FEATURES = {
  one: () => 1,
  lnR: (x) => Math.log(Math.max(0, x.totalMm) + EPS_MM),
  lnR_lo: (x) => Math.max(0, LN_1 - Math.log(Math.max(0, x.totalMm) + EPS_MM)),
  lnR_hi: (x) => Math.max(0, Math.log(Math.max(0, x.totalMm) + EPS_MM) - LN_10),
  lnH: (x) => Math.log(x.windowHours),
  rng: (x) => x.rangeKm / 100,
  rng_near: (x) => Math.max(0, 50 - x.rangeKm) / 50,
  rng_far: (x) => Math.max(0, x.rangeKm - 150) / 100,
  cv: (x) => x.localCv,
  conv: (x) => x.convFrac,
  lnP99: (x) => Math.log(1 + x.rateP99Wet),
  lnP50: (x) => Math.log(1 + x.rateP50Wet),
  lnP90: (x) => Math.log(1 + x.rateP90Wet),
  terrain: (x) => Math.min(x.beamRaiseDeg, TERRAIN_CAP_DEG),
  wet: (x) => x.wetAreaFrac,
  twet: (x) => x.totalWetFrac,
  cool: (x) => x.coolSeason,
  scos: (x) => x.seasonCos,
  gap: (x) => x.gapFraction,
  dt: (x) => x.meanScanIntervalMin / 10,
  lat: (x) => (x.stationLat - 37) / 10,
};

const levelKey = (p) => `q${String(Math.round(p * 100)).padStart(2, "0")}`;

function interp(xs, ys, x) {
  if (x <= xs[0]) return ys[0];
  const n = xs.length - 1;
  if (x >= xs[n]) return ys[n];
  let i = 0;
  while (x > xs[i + 1]) i++;
  const f = (x - xs[i]) / (xs[i + 1] - xs[i]);
  return ys[i] + f * (ys[i + 1] - ys[i]);
}

/**
 * Model quantiles of q = ln((G + eps)/(R + eps)) at the model's own levels,
 * after the conformal shift and rearrangement. Exposed for the harness.
 * Returns { q: number[], extrapolated, outside: string[], missing: string[] }.
 */
export function predictLogRatio(model, inputs) {
  const x = {};
  const outside = [];
  const missing = [];
  for (const [name, s] of Object.entries(model.support)) {
    let v = inputs[name];
    if (typeof v !== "number" || !Number.isFinite(v)) {
      missing.push(name);
      v = s.median;
    } else if (v < s.min || v > s.max) {
      outside.push(name);
      v = Math.min(s.max, Math.max(s.min, v));
    }
    x[name] = v;
  }
  const f = model.features.map((name) => {
    const fn = FEATURES[name];
    if (!fn) throw new Error(`errorModel: unknown feature ${name}`);
    return fn(x);
  });
  const lnR = FEATURES.lnR(x);
  const q = model.coef.map((row, k) => {
    let s = 0;
    for (let j = 0; j < row.length; j++) s += row[j] * f[j];
    return s + interp(model.shift.knotsLnR, model.shift.delta[k], lnR);
  });
  q.sort((a, b) => a - b);
  return { q, extrapolated: outside.length > 0 || missing.length > 0, outside, missing };
}

/**
 * Interval for the rain a gauge at this cell would have caught, in mm.
 *   inputs: { totalMm, ...cellCovariates(), ...computeRunCovariates() }
 * Returns { q05, q25, q50, q75, q95, extrapolated, outside, missing } (keys
 * follow `levels`), each >= 0 and non-decreasing (values under half a gauge
 * tip, eps/2 = 0.127 mm, are reported as 0), or null when totalMm is not
 * a number (outside radar coverage). Levels between the model's are
 * interpolated in q; levels beyond its range are clamped to the end levels.
 * `extrapolated` is true when an input was outside the training support
 * (it is clamped to the support edge) or missing (training median used).
 */
export function rainInterval(model, inputs, { levels = [0.05, 0.25, 0.5, 0.75, 0.95] } = {}) {
  const R = inputs?.totalMm;
  if (typeof R !== "number" || !Number.isFinite(R)) return null;
  const { q, extrapolated, outside, missing } = predictLogRatio(model, inputs);
  const base = Math.max(0, R) + model.epsMm;
  const out = {};
  let prev = 0;
  for (const p of [...levels].sort((a, b) => a - b)) {
    const qp = interp(model.levels, q, p);
    let g = base * Math.exp(qp) - model.epsMm;
    // Below half a gauge tip (0.127 mm) a gauge reads 0: report 0, not a
    // spurious "at least 0.05 mm" lower bound.
    if (g < model.epsMm / 2) g = 0;
    g = Math.max(prev, g);
    out[levelKey(p)] = g;
    prev = g;
  }
  out.extrapolated = extrapolated;
  out.outside = outside;
  out.missing = missing;
  return out;
}

/** Inputs the model actually uses (for callers that want to skip the rest). */
export function modelInputs(model) {
  return Object.keys(model.support);
}
